import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { API, imgUrl } from "../lib/storeApi";

export const useGallery = (category) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    fetch(`${API}/gallery${category ? `?category=${category}` : ""}`).then((r) => r.json()).then(setItems).catch(() => setItems([]));
  }, [category]);
  return items;
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const Shot = ({ src, label, onOpen, testId, ratio = "aspect-[4/3]" }) => (
  <button type="button" onClick={() => onOpen(src)} data-testid={testId} className={`relative block w-full ${ratio} overflow-hidden bg-slate-100 group`}>
    <img src={imgUrl(src)} alt={label ? `${label} photo` : "Install photo"} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
    {label && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[11px] font-bold uppercase tracking-wide">{label}</span>}
  </button>
);

const Card = ({ item, onOpen, idx }) => (
  <figure data-testid={`gallery-item-${idx}`} className="rounded-2xl overflow-hidden border border-slate-200 bg-white">
    {item.before_url ? (
      <div className="grid grid-cols-2 gap-px bg-slate-200 aspect-[4/3]">
        <Shot src={item.before_url} label="Before" ratio="h-full" onOpen={onOpen} testId={`gallery-item-${idx}-before`} />
        <Shot src={item.after_url} label="After" ratio="h-full" onOpen={onOpen} testId={`gallery-item-${idx}-after`} />
      </div>
    ) : (
      <Shot src={item.after_url} onOpen={onOpen} testId={`gallery-item-${idx}-after`} />
    )}
    <figcaption className="p-4">
      {item.caption && <p data-testid={`gallery-item-${idx}-caption`} className="font-semibold text-[#0a1f44]">{item.caption}</p>}
      <p data-testid={`gallery-item-${idx}-date`} className="text-xs text-slate-500 mt-1">{fmtDate(item.created_at)}</p>
    </figcaption>
  </figure>
);

export const GalleryGrid = ({ items }) => {
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
        {items.map((it, i) => <Card key={it.id} item={it} idx={i} onOpen={setOpen} />)}
      </div>
      {open && (
        <div data-testid="gallery-lightbox" onClick={() => setOpen(null)} className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4">
          <button type="button" data-testid="gallery-lightbox-close" onClick={() => setOpen(null)} aria-label="Close" className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="w-8 h-8" /></button>
          <img src={imgUrl(open)} alt="Install photo enlarged" data-testid="gallery-lightbox-img" onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </>
  );
};

export const TownInstalls = ({ town, city }) => {
  const items = useGallery();
  if (!items || items.length === 0) return null;
  const local = items.filter((x) => x.town === town);
  const tv = items.filter((x) => x.category !== "audio");
  const shown = (local.length ? local : tv.length ? tv : items).slice(0, 6);
  return (
    <section data-testid="town-installs" className="section-pad bg-[#f5f8ff] tex-grid-light">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
        <span className="eyebrow">Recent installs</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f44]" data-testid="town-installs-heading">
          {local.length ? `Real installs in ${city}` : "Real installs from across Eastern NC"}
        </h2>
        <div className="mt-10"><GalleryGrid items={shown} /></div>
        <Link to="/recent-installs" data-testid="town-installs-view-all" className="mt-8 inline-flex items-center gap-2 font-bold text-[#3b9dff] hover:text-[#2b8ae8]">
          See all recent installs <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export const FireplaceInstalls = () => {
  const items = useGallery("tv");
  if (!items || items.length === 0) return null;
  const fp = items.filter((x) => x.fireplace);
  const shown = (fp.length ? fp : items).slice(0, 6);
  return (
    <section data-testid="fireplace-installs" className="section-pad bg-[#f5f8ff] tex-grid-light">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
        <span className="eyebrow">{fp.length ? "Fireplace installs" : "Recent installs"}</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f44]" data-testid="fireplace-installs-heading">
          {fp.length ? "Fireplace TV installs, before and after" : "Real installs from our crew"}
        </h2>
        <div className="mt-10"><GalleryGrid items={shown} /></div>
        <Link to="/recent-installs" data-testid="fireplace-installs-view-all" className="mt-8 inline-flex items-center gap-2 font-bold text-[#3b9dff] hover:text-[#2b8ae8]">
          See all recent installs <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export const HomeInstallGallery = ({ category, testId = "home-recent-installs", heading = "Real installs from our crew" }) => {
  const items = useGallery(category);
  if (!items || items.length === 0) return null;
  return (
    <section data-testid={testId} className="section-pad bg-[#f5f8ff] tex-grid-light">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
        <span className="eyebrow">Recent installs</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f44]">{heading}</h2>
        <div className="mt-10"><GalleryGrid items={items.slice(0, 6)} /></div>
        <Link to="/recent-installs" data-testid={`${testId}-view-all`} className="mt-8 inline-flex items-center gap-2 font-bold text-[#3b9dff] hover:text-[#2b8ae8]">
          See all recent installs <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};
